// BFSG
import * as React from 'react'
import styled from 'styled-components'
import theme from 'theme'
import ProductImage from 'theme/atoms/ProductImage'
import whitePixel from 'utils/whitePixel'
import Price from 'theme/molecules/Price'
import Button from 'theme/atoms/Button'
import { ReactComponent as ArrowButtonRight } from 'assets/arrow-button-right.svg'
import Brand from './Brand'
import Flags from 'theme/molecules/Flags'
import useProduct, { Product } from './useProduct'
import Markdown from 'theme/atoms/Markdown'
import getProductLink from 'utils/getProductLink'
import Rating from 'theme/molecules/Rating'

export type Props = {
  sku: string
  brandPath: string
  title?: React.ReactNode
  btnLabel: string
  product?: Product
  'data-cy-state'?: string
}

export default function ProductHighlight(props: Props) {
  const { fetching, data: productData } = useProduct(
    !props.product ? props.sku : ''
  )
  const product = props.product ?? productData

  if (!fetching && !product) return null

  const pdpLink = product ? getProductLink(product) : ''

  return (
    <Wrapper
      className="ProductHighlight"
      data-cy-ctx="molecules/HighlightProduct"
      data-cy-state={props['data-cy-state']}
    >
      {props.title && (
        <div data-cy-state="show-title" className="title">
          {props.title}
        </div>
      )}
      <div className="content">
        <div className="image">
          {product && (
            <span data-cy-state="render:flags">
              <Flags product={product} type={'widget'} />
            </span>
          )}
          {product && (
            <ProductImage
              data-cy-state="render:show-image"
              alt={product.title}
              img={product.images.imageWeb[0]}
              size="s"
            />
          )}
          {!product && (
            <ProductImage
              data-cy-state="placeholder:show-image"
              img={{
                url: whitePixel,
                classes: ['ASSET_FS']
              }}
              size="s"
              alt=""
            />
          )}
        </div>
        <div className="info">
          {product && (
            <Brand
              data-cy-state="render:brand"
              brandPath={props.brandPath}
              product={product}
            />
          )}
          <div className="title">{product?.title ?? ' '}</div>
          <Rating
            containerID={product?.containerID ?? ''}
            ratingAverage={product?.productReviews.productReviewAvg}
            ratingCount={product?.productReviews.productReviewCount}
          />
          {product && (
            <>
              <Price
                data-cy-state="render:show-product-price"
                prices={product.prices}
                logic="piece"
                unit={product.unit}
                options={{
                  hideEcoTax: true,
                  amount: 1,
                  pageType: 'Other',
                  showSubline: product.unit.purchaseUnit > 1,
                  specialType: 'HighlightProduct'
                }}
              />
            </>
          )}
          <div className="description">
            <Markdown html={product?.description ?? ' '} />
          </div>
        </div>
        <div className="buttons">
          <Button
            variation="secondary"
            to={pdpLink}
            $fullWidth
            data-cy-handle="product-link"
            aria-label={`${props.btnLabel}: ${product?.title ?? ''}`}
          >
            {props.btnLabel} <ArrowButtonRight />
          </Button>
        </div>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  > .title {
    ${theme.ty('rc-2xl', null, 'rc-3xl')};
    margin-bottom: ${theme.spacing('m')};
  }

  > .content {
    background: ${theme.colors.shade.b7};
    padding: ${theme.spacing('m')};
    display: grid;
    grid-template:
      'image info'
      'buttons buttons'
      / 2fr 3fr;
    grid-column-gap: ${theme.spacing('xs')};
    grid-row-gap: ${theme.spacing('m')};

    @media (min-width: ${theme.ms.MD}px) {
      padding: ${theme.spacing('l')};
      grid-template:
        'image info'
        'image buttons'
        'image .'
        / auto 1fr;
      grid-column-gap: ${theme.spacing('m')};
    }

    > .image {
      grid-area: image;
      background: ${theme.colors.white};
      padding: ${theme.spacing('xxs')};
      position: relative;
    }

    > .info {
      grid-area: info;
      margin-bottom: ${theme.spacing('xxs')};

      @media (min-width: ${theme.ms.MD}px) {
        margin-bottom: ${theme.spacing('xs')};
      }

      > .title {
        margin-bottom: ${theme.spacing('xs')};
        ${theme.ty('rc-base', null, 'rc-2xl')};
      }
      > .Rating {
        margin-bottom: ${theme.spacing('xs')};
      }

      > .description {
        display: none;
        height: 80px; // exact 3 lines height
        overflow: hidden;
        position: relative;
        margin-top: ${theme.spacing('xs')};

        @media (min-width: ${theme.ms.MD}px) {
          display: block;
        }

        p:first-child {
          margin-top: 0 !important;
        }

        &:after {
          content: '';
          position: absolute;
          bottom: 0;
          height: 60px;
          left: 0;
          right: 0;
          background: linear-gradient(
            180deg,
            transparent 0%,
            rgba(249, 249, 248, 1) 90%
          );
        }
      }
    }

    > .buttons {
      grid-area: buttons;
      display: flex;

      > .Button {
        width: 100%;

        @media (min-width: ${theme.ms.MD}px) {
          width: auto;
        }
      }
    }
  }
`
